import React from 'react';
import FusionLogo from '../components/FusionLogo';

// Soft green "bokeh" orbs — out-of-focus glowing circles scattered across a
// near-black field, matching the approval-portal login look. Pure CSS: each
// orb is a blurred radial-gradient disc. Larger discs are heavily blurred for
// depth; the smaller, less-blurred ones add the in-focus "sparkle" the fully-
// soft discs lack. Static, pointer-events off, sits behind the auth card.
// Density + spread here are tuned to match the Fusion approval-portal login
// reference (a full, rich field across the whole viewport — the card's own
// #18181A surface sits above and dims any orb that falls behind it).
const orbs = [
  // Large, heavily-blurred anchors toward the corners.
  { top: '-14%', left: '-10%', size: 460, core: 0.9, blur: 115, opacity: 0.15 },
  { top: '-8%',  left: '66%',  size: 400, core: 0.9, blur: 115, opacity: 0.12 },
  { top: '60%',  left: '-12%', size: 420, core: 0.9, blur: 115, opacity: 0.13 },
  { top: '70%',  left: '78%',  size: 380, core: 0.9, blur: 115, opacity: 0.11 },
  // Medium mid-field discs fill the gaps.
  { top: '8%',   left: '34%',  size: 260, core: 0.88, blur: 95,  opacity: 0.08 },
  { top: '40%',  left: '60%',  size: 300, core: 0.9,  blur: 100, opacity: 0.09 },
  { top: '78%',  left: '30%',  size: 300, core: 0.9,  blur: 100, opacity: 0.12 },
  { top: '84%',  left: '54%',  size: 220, core: 0.9,  blur: 85,  opacity: 0.10 },
  { top: '20%',  left: '84%',  size: 200, core: 0.9,  blur: 85,  opacity: 0.09 },
  { top: '30%',  left: '6%',   size: 210, core: 0.9,  blur: 85,  opacity: 0.10 },
  { top: '88%',  left: '10%',  size: 240, core: 0.9,  blur: 90,  opacity: 0.09 },
  // Smaller, sharper "sparkle" discs — a touch of in-focus detail.
  { top: '50%',  left: '40%',  size: 150, core: 0.95, blur: 55,  opacity: 0.06 },
  { top: '14%',  left: '50%',  size: 140, core: 0.95, blur: 52,  opacity: 0.07 },
  { top: '64%',  left: '66%',  size: 150, core: 0.95, blur: 52,  opacity: 0.08 },
];

export const BokehBackground = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {orbs.map((o, i) => (
      <div
        key={i}
        className="absolute rounded-full"
        style={{
          top: o.top,
          left: o.left,
          width: `${o.size}px`,
          height: `${o.size}px`,
          background: `radial-gradient(circle at 50% 45%, rgba(146,212,0,${o.core}) 0%, rgba(120,156,30,${o.core * 0.45}) 45%, rgba(0,0,0,0) 72%)`,
          filter: `blur(${o.blur}px)`,
          opacity: o.opacity,
        }}
      />
    ))}
  </div>
);

// Logo lockup mark — the mark-only Fusion hexagon (/fusion-icon.svg),
// sized small for the login lockup (guideline §7: ~44px hex + FUSION
// wordmark). Same asset the sidebar uses, so login + in-app stay in
// lockstep.
export const FusionMark = ({ className }) => (
  <FusionLogo icon alt="Fusion" className={className} />
);

// Shared Fusion login frame (brand-guideline §4). One continuous
// #0B0B0C canvas; the card + logo lockup + confidential line form a
// single vertically-centred stack (no dead space below the card). The
// CORE badge is the only viewport-pinned element.
export const AuthShell = ({ children }) => (
  <div className="min-h-screen bg-[#0B0B0C] flex flex-col items-center justify-center relative overflow-hidden p-6">
    <BokehBackground />
    {children}
    {/* Logo lockup — hexagon mark (~44px) above the FUSION wordmark,
        centred 36px below the card. */}
    <div className="relative mt-9 flex flex-col items-center gap-2">
      <FusionMark className="w-11 h-11 object-contain" />
      <span className="font-['Metropolis'] text-sm font-bold tracking-[0.34em] text-[#F1F1F3] pl-[0.34em]">
        FUSION
      </span>
    </div>
    {/* Build #18 §7b: dropped the "Fusion 2K Ltd — Confidential" line —
        "Confidential" is internal language and these auth screens are
        supplier-facing. */}
    {/* Powered-by-CORE badge — pinned bottom-right of the viewport with
        an angled corner (family login frame, §4/§7). */}
    <div
      className="fixed right-0 bottom-0 z-[2] flex items-center gap-1.5 py-2 pl-[26px] pr-3.5 font-['Metropolis'] text-[9px] tracking-[0.02em] text-[#7A7A80]"
      style={{ background: 'linear-gradient(135deg, transparent 14px, #161616 14px)' }}
    >
      Powered by <b className="font-bold tracking-[0.16em] text-[#C9C9CE]">CORE</b>
    </div>
  </div>
);

// Shared auth input (Fusion login frame — identical across the family).
// Build #18 §3c: the input fill was #E9EAF4 (a pale lilac — reads as
// banned purple). Swapped for a neutral white surface with a #D4D9C6
// hairline so it stays distinct on the white auth card. Visible green
// focus ring kept.
export const authInputClass = "w-full bg-white border border-[#D4D9C6] text-[#131313] placeholder-[#7C7F6F] px-4 py-3 font-['Metropolis'] text-[15px] focus:outline-none focus-visible:ring-2 focus-visible:ring-fusion-green rounded-[10px]";
// Build #18 §3b: was green text (#92D400) on a translucent grey fill —
// read as disabled / half-styled. Rebuilt as a proper solid-green primary
// (acid fill + dark #16250A label, 8.9:1 contrast). Full-width action bar.
export const authSubmitClass = "w-full bg-fusion-green font-['Metropolis'] text-base font-bold text-[#16250A] py-5 hover:brightness-95 transition disabled:opacity-50";
